/**
 * Unit systems, and the one place that knows how to convert between them.
 *
 * Everything the server stores and computes is metric: metres, grams per cubic
 * centimetre, fractions. That does not change with this setting, and it should
 * not — a stored number that means different things depending on who is looking
 * is a number nobody can check. Conversion happens here, at the edge, on the
 * way to the screen.
 *
 * **What actually changes, and what does not.** "Imperial" in log work is not a
 * blanket rule; it is a specific set of conventions, and applying it to
 * everything would produce numbers no petrophysicist has ever written down:
 *
 * | Quantity | Metric | Imperial (oilfield) |
 * |---|---|---|
 * | Depth, thickness, net pay | m | ft |
 * | Map distance | m / km | ft / miles |
 * | Sonic slowness | µs/m | µs/ft |
 * | Bulk density | g/cm³ | g/cm³ — unchanged |
 * | Porosity, Vsh, Sw | v/v | v/v — unchanged |
 * | Resistivity | ohm·m | ohm·m — unchanged |
 * | Gamma ray | gAPI | gAPI — unchanged |
 * | Permeability | mD | mD — unchanged |
 *
 * Density stays in g/cm³ and resistivity in ohm·m because that is what the
 * industry writes in both systems. Converting them to lb/ft³ would be
 * arithmetically correct and practically useless.
 */

export type UnitSystem = "metric" | "imperial";

export const METRES_TO_FEET = 3.280839895;
export const METRES_TO_MILES = 1 / 1609.344;

/** Curves whose values are rescaled, keyed by canonical mnemonic. */
const CURVE_FACTORS: Record<string, { factor: number; unit: string }> = {
  // Sonic slowness is per unit length, so it converts the other way round:
  // µs per metre becomes µs per foot by dividing, not multiplying.
  DTC: { factor: 1 / METRES_TO_FEET, unit: "us/ft" },
  DTS: { factor: 1 / METRES_TO_FEET, unit: "us/ft" },
};

export interface Units {
  system: UnitSystem;
  /** Short name of the depth unit: "m" or "ft". */
  depthUnit: string;
  /** Metres to the display unit. */
  depth: (metres: number) => number;
  /** Display unit back to metres — for anything the user types in. */
  toMetres: (value: number) => number;
  /** A depth or thickness with its unit, e.g. "1885 m" or "6184 ft". */
  formatDepth: (metres: number | null | undefined, digits?: number) => string;
  /** A map distance, switching to km or miles once it gets long. */
  formatDistance: (metres: number | null | undefined) => string;
  /** Rescale one curve's values for display, if that curve converts at all. */
  curve: (mnemonic: string, value: number) => number;
  /** The unit label a curve carries on screen. */
  curveUnit: (mnemonic: string, fallback: string) => string;
}

export function unitsFor(system: UnitSystem | string | null | undefined): Units {
  const imperial = system === "imperial";
  const depth = (m: number) => (imperial ? m * METRES_TO_FEET : m);

  return {
    system: imperial ? "imperial" : "metric",
    depthUnit: imperial ? "ft" : "m",
    depth,
    toMetres: (v: number) => (imperial ? v / METRES_TO_FEET : v),
    formatDepth: (metres, digits = 0) =>
      metres === null || metres === undefined || !Number.isFinite(metres)
        ? "—"
        : `${depth(metres).toFixed(digits)} ${imperial ? "ft" : "m"}`,
    formatDistance: (metres) => {
      if (metres === null || metres === undefined || !Number.isFinite(metres)) return "—";
      if (imperial) {
        const miles = metres * METRES_TO_MILES;
        // Below a tenth of a mile, feet read better than "0.06 mi".
        return miles < 0.1
          ? `${(metres * METRES_TO_FEET).toFixed(0)} ft`
          : `${miles.toFixed(miles < 10 ? 1 : 0)} mi`;
      }
      return metres < 1000 ? `${metres.toFixed(0)} m` : `${(metres / 1000).toFixed(1)} km`;
    },
    curve: (mnemonic, value) => {
      const rule = imperial ? CURVE_FACTORS[mnemonic.toUpperCase()] : undefined;
      return rule ? value * rule.factor : value;
    },
    curveUnit: (mnemonic, fallback) => {
      const rule = imperial ? CURVE_FACTORS[mnemonic.toUpperCase()] : undefined;
      return rule ? rule.unit : fallback;
    },
  };
}

/** The two choices, for a selector. */
export const UNIT_SYSTEMS: { value: UnitSystem; label: string; note: string }[] = [
  {
    value: "metric",
    label: "SI (metric)",
    note: "Depths and thicknesses in metres, sonic in µs/m.",
  },
  {
    value: "imperial",
    label: "British (oilfield)",
    note: "Depths and thicknesses in feet, sonic in µs/ft. Density stays g/cm³ "
      + "and resistivity ohm·m, as both systems write them.",
  },
];
